import React from "react";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
} from "recharts";

const COLORS = {
  approved: "#059669",
  active: "#059669",
  pending: "#D97706",
  rejected: "#DC2626",
  submitted: "#1E3A8A",
  reviewed: "#0891B2",
  accepted: "#7C3AED",
  agreement_sent: "#0891B2",
  "agreement sent": "#0891B2",
  agreement_signed: "#6366F1",
  "agreement signed": "#6366F1",
};

export function ChartCard({ title, subtitle, children, delay = 0, action }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </motion.div>
  );
}

export function StatusDonut({ data }) {
  // data: [{name, value}]
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return (
      <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">
        No data yet
      </div>
    );
  }
  return (
    <div className="relative h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={70}
            outerRadius={100}
            paddingAngle={3}
            dataKey="value"
            stroke="#fff"
            strokeWidth={2}
            isAnimationActive
            animationDuration={900}
          >
            {data.map((d) => (
              <Cell
                key={d.name}
                fill={COLORS[d.name.toLowerCase()] || "#1E3A8A"}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-3xl font-bold text-slate-900 font-display">
          {total}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Total
        </div>
      </div>
      <div className="flex flex-wrap gap-3 justify-center mt-2">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-1.5 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{
                background: COLORS[d.name.toLowerCase()] || "#1E3A8A",
              }}
            />
            <span className="capitalize text-slate-700">{d.name}</span>
            <span className="text-slate-400">({d.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProgramsBar({ data }) {
  if (!data.length) {
    return (
      <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">
        No programs selected yet
      </div>
    );
  }
  return (
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 0, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis
            type="category"
            dataKey="name"
            width={140}
            tick={{ fontSize: 11, fill: "#475569" }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
            }}
          />
          <defs>
            <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#1E3A8A" />
              <stop offset="100%" stopColor="#C49A3A" />
            </linearGradient>
          </defs>
          <Bar
            dataKey="value"
            fill="url(#barGrad)"
            radius={[0, 6, 6, 0]}
            isAnimationActive
            animationDuration={900}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TimelineChart({ data, series = ["value"], colors = ["#1E3A8A"] }) {
  return (
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis
            tick={{ fontSize: 11, fill: "#64748b" }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
            }}
          />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.map((s, i) => (
            <Line
              key={s}
              type="monotone"
              dataKey={s}
              stroke={colors[i] || "#1E3A8A"}
              strokeWidth={2.5}
              dot={{ r: 3, fill: colors[i] }}
              activeDot={{ r: 6 }}
              isAnimationActive
              animationDuration={900}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
